/**
 * Подключение к Yjs без CodeMirror — для graph/tree/spreadsheet workspace.
 */
/* eslint-disable react-hooks/refs */
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { whenYjsSynced } from '../../utils/whenYjsSynced';

const CollaborativeYdocHost = forwardRef(function CollaborativeYdocHost({
  sessionId,
  onYdocReady,
  onStatusChange,
  reconnectNonce = 0,
  children,
}, ref) {
  const [ydoc, setYdoc] = useState(null);
  const [awareness, setAwareness] = useState(null);
  const [connected, setConnected] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const onYdocReadyRef = useRef(onYdocReady);
  const onStatusChangeRef = useRef(onStatusChange);
  const providerRef = useRef(null);
  onYdocReadyRef.current = onYdocReady;
  onStatusChangeRef.current = onStatusChange;

  useImperativeHandle(ref, () => ({
    reconnect: () => {
      const provider = providerRef.current;
      if (!provider) return;
      try {
        provider.disconnect();
      } catch {
        // ignore
      }
      try {
        provider.connect();
      } catch {
        // ignore
      }
    },
  }), []);

  useEffect(() => {
    if (typeof onStatusChangeRef.current === 'function') {
      onStatusChangeRef.current({ connected, userCount });
    }
  }, [connected, userCount]);

  useEffect(() => {
    if (!reconnectNonce) return;
    const provider = providerRef.current;
    if (!provider) return;
    try {
      provider.disconnect();
    } catch {
      // ignore
    }
    try {
      provider.connect();
    } catch {
      // ignore
    }
  }, [reconnectNonce]);

  useEffect(() => {
    if (!sessionId) return undefined;

    const doc = new Y.Doc();
    const isSecure = window.location.protocol === 'https:';
    const wsScheme = isSecure ? 'wss' : 'ws';
    const serverUrl = `${wsScheme}://${window.location.host}/ws/collaborative`;
    const provider = new WebsocketProvider(serverUrl, sessionId, doc);
    providerRef.current = provider;

    provider.on('status', (event) => {
      setConnected(event.status === 'connected');
    });
    provider.awareness.on('change', () => {
      setUserCount(provider.awareness.getStates().size);
    });

    let ready = false;
    const markReady = () => {
      if (ready) return;
      ready = true;
      setYdoc(doc);
      setAwareness(provider.awareness);
      if (onYdocReadyRef.current) {
        onYdocReadyRef.current(doc, { awareness: provider.awareness });
      }
    };

    const unsubSync = whenYjsSynced(provider, markReady);
    // Локально / без yws: не висеть на «Подключение…» — правим лист без комнаты.
    const fallbackTimer = window.setTimeout(() => {
      if (!provider.synced) markReady();
    }, 2500);

    return () => {
      window.clearTimeout(fallbackTimer);
      unsubSync();
      if (onYdocReadyRef.current) onYdocReadyRef.current(null);
      provider.destroy();
      doc.destroy();
      providerRef.current = null;
      setYdoc(null);
      setAwareness(null);
      setConnected(false);
      setUserCount(0);
    };
  }, [sessionId]);

  if (!sessionId || !ydoc) {
    return (
      <div className="editor-connecting">
        <div className="spinner" />
        <p>Подключение…</p>
      </div>
    );
  }

  return typeof children === 'function'
    ? children({ ydoc, awareness, connected, userCount })
    : children;
});

export default CollaborativeYdocHost;
